import { useCallback, useEffect } from "react"

import { useQueryClient } from "@tanstack/react-query"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"

import type { CrossTabFactCheckEvent } from "@/features/chat/utils/fact-check-cross-tab"

import { useRoutes } from "@/contexts/use-routes"
import { subscribeCrossTab } from "@/features/chat/utils/fact-check-cross-tab"
import { FactCheckJobManager } from "@/features/chat/utils/fact-check-job-manager"
import { queryKeys } from "@/lib/query-keys"

function canShowBrowserNotification(): boolean {
  try {
    return (
      typeof window !== "undefined" &&
      "Notification" in window &&
      Notification.permission === "granted"
    )
  } catch {
    return false
  }
}

export function FactCheckBackgroundListener() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { getRouteChatPath } = useRoutes()

  const invalidateAndNavigate = useCallback(
    async (sessionId: string, openUrl: string) => {
      const expectedKey = queryKeys.sessionMessages(sessionId)
      try {
        await queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey
            return (
              Array.isArray(key) &&
              key.length >= 2 &&
              key[0] === expectedKey[0] &&
              key[1] === expectedKey[1]
            )
          },
        })
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to invalidate session messages before navigation", error)
      } finally {
        void navigate(openUrl)
      }
    },
    [navigate, queryClient]
  )

  const buildDeepLinkUrl = useCallback(
    (sessionType: string, sessionId: string, messageId: string) => {
      const url = new URL(`${getRouteChatPath(sessionType)}/${sessionId}`, window.location.origin)
      url.searchParams.set("factCheckMessageId", messageId)
      url.searchParams.set("factCheckUpdatedAt", `${Date.now()}`)
      return `${url.pathname}?${url.searchParams.toString()}`
    },
    [getRouteChatPath]
  )

  useEffect(() => {
    const manager = FactCheckJobManager.getInstance()

    const unsubscribeManager = manager.subscribe((event) => {
      const { sessionId, sessionType, messageId } = event

      if (event.type === "fact_check_error") {
        toast.error(t("fact_check.failed"), {
          description: event.error.message || t("fact_check.error_occurred"),
        })
        return
      }

      if (!sessionId || !messageId) {
        toast.success(t("fact_check.completed"))
        return
      }

      const openUrl = buildDeepLinkUrl(sessionType, sessionId, messageId)

      if (document.hidden && canShowBrowserNotification()) {
        try {
          const notif = new Notification(t("fact_check.completed"), {
            body: t("fact_check.tap_to_view"),
          })
          notif.onclick = async () => {
            try {
              window.focus()
            } catch {
              // ignore
            }
            if (document.hasFocus()) {
              await invalidateAndNavigate(sessionId, openUrl)
            }
            notif.close()
          }
        } catch {
          // ignore
        }
      }

      toast.success(t("fact_check.completed"), {
        description: t("fact_check.click_to_view"),
        duration: 15000,
        action: {
          label: t("fact_check.view_action"),
          onClick: () => void invalidateAndNavigate(sessionId, openUrl),
        },
      })
    })

    const unsubscribeCrossTab = subscribeCrossTab((event: CrossTabFactCheckEvent) => {
      const { sessionId, sessionType, messageId, type, errorMessage } = event

      if (type === "error") {
        toast.error(t("fact_check.failed"), {
          description: errorMessage || t("fact_check.error_occurred"),
        })
        return
      }

      if (!sessionId || !messageId) {
        toast.success(t("fact_check.completed"))
        return
      }

      const openUrl = buildDeepLinkUrl(sessionType, sessionId, messageId)

      toast.success(t("fact_check.completed"), {
        description: t("fact_check.click_to_view"),
        duration: 15000,
        action: {
          label: t("fact_check.view_action"),
          onClick: () => void invalidateAndNavigate(sessionId, openUrl),
        },
      })
    })

    return () => {
      unsubscribeManager()
      unsubscribeCrossTab()
    }
  }, [t, getRouteChatPath, invalidateAndNavigate, buildDeepLinkUrl])

  return null
}
